import { anonymizeTools } from '../../tools/Identity';
import { AuthTokenStrategy } from './AuthTokenStrategy';
import { composeQueryString } from '../../lib/Query';
import { RequestHeaders } from '../Request/RequestHeaders';
import { SignInData } from './types';
import ChromeExtensionService from '../ChromeExtension/ChromeExtensionService';

class AuthTokenProvider {
  private strategy: AuthTokenStrategy | undefined;

  public setStrategy(strategy: AuthTokenStrategy) {
    this.strategy = strategy;
  }

  public getAuthenticationHeader(): RequestHeaders {
    this.validateStrategy();
    return this.strategy!.getAuthenticationHeader();
  }

  public getAuthenticationToken(): string | undefined {
    this.validateStrategy();
    return this.strategy!.getAuthenticationToken();
  }

  public isLoggedIn(): boolean {
    return this.getAuthenticationToken() !== undefined;
  }

  public signIn(payload: SignInData, forceOverwrite?: boolean): void {
    this.validateStrategy();
    this.strategy!.signIn(payload, forceOverwrite);
  }

  public signOut(query?: Record<string, any>): Promise<void> {
    this.validateStrategy();
    return this.performSignOut(query);
  }

  private validateStrategy() {
    if (!this.strategy) {
      throw new Error('AuthTokenProvider:: Strategy not set');
    }
  }

  /**
   * Performs all the steps required for actually signing out the user from
   * the app. This includes notifying the chrome extension, anonymizing
   * tracking tools, preparing the URL for refreshing and actually destroying
   * the session token from the strategy
   */
  private performSignOut(query = {}): Promise<void> {
    return new Promise((resolve) => {
      /**
       * Notifying the chrome extension via its messaging API to let it know
       * the user has been signed out. It's up to the chrome extension to dispose
       * the access token properly.
       */
      new ChromeExtensionService().notifySignOut();

      // Sign out will reload, so tools must be anonymized before that
      anonymizeTools();

      // Timeout is added to mitigate race conditions between event listeners
      // and sign out effects (like removing localStorage keys/tokens)
      setTimeout(() => {
        // Set a query param to allow other services react to this flow
        // ie. avoid setting a next location for the login screen on sign out
        const queryParams = composeQueryString({ signOut: true, ...query });
        const url = `${window.location.pathname}${queryParams}`;
        // @warn there's no way to get the history or navigation as those
        // are hooks, so this is the easiest way, although it's an exception
        window.history.replaceState(window.history.state, '', url);

        this.strategy!.signOut();
        resolve();
      }, 1000);
    });
  }
}

const AuthTokenProviderInstance = new AuthTokenProvider();
export default AuthTokenProviderInstance;
